/* UI culture belongs to the page; API values and stored codes remain invariant. */
(() => {
  'use strict';
  if (globalThis.UnionStackI18n) return;
  const messages = () => globalThis.UnionStackUi?.messages || {};
  const format = (template, args) => String(template).replace(/\{\{|\}\}|\{(\d+)(?:[^{}]*)?\}/g,
    (token, index) => token === '{{' ? '{' : token === '}}' ? '}' : index < args.length ? String(args[index]) : token);
  const text = (key, ...args) => {
    const template = messages()[key];
    if (typeof template !== 'string') {
      globalThis.dispatchEvent?.(new CustomEvent('unionstack:missing-message', {detail: {key}}));
      return messages()['Common.MessageMissing'] || `[${key}]`;
    }
    return format(template, args);
  };
  const statusCode = status => ({400:'validation_failed',401:'session_expired',403:'access_denied',404:'record_not_found',
    408:'request_timeout',409:'conflict',412:'precondition_failed',428:'precondition_required',429:'rate_limited',503:'dependency_unavailable'})[status] || 'request_failed';
  const descriptor = (value, fallback) => {
    const code = typeof value?.code === 'string' ? value.code : fallback;
    const mapping = globalThis.UnionStackUi?.problemCodes?.[code];
    const key = mapping?.Key ?? mapping?.key;
    const names = mapping?.Parameters ?? mapping?.parameters ?? [];
    if (typeof key !== 'string' || !Array.isArray(names)) return text('Common.MessageRequestFailed');
    const parameters = value?.parameters || {};
    if (names.some(name => ['maximum', 'minimum'].includes(name) && typeof parameters[name] !== 'number'))
      return text('Common.MessageValidation');
    const args = names.map(name => parameters[name]);
    if (args.some(arg => !['string','number','boolean'].includes(typeof arg)
      || (typeof arg === 'number' && !Number.isFinite(arg)) || String(arg).length > 2000)) return text('Common.MessageValidation');
    return text(key, ...args);
  };
  const problem = (value, status = 500) => {
    const fallback = statusCode(status);
    const known = globalThis.UnionStackUi?.problemCodes;
    const code = typeof value?.code === 'string' && Object.hasOwn(known || {}, value.code) ? value.code : fallback;
    const fields = Object.create(null);
    if (value?.errors && typeof value.errors === 'object' && !Array.isArray(value.errors)) {
      for (const field of Object.keys(value.errors)) {
        const definitions = value.validationCodes?.[field];
        fields[field] = Array.isArray(definitions) && definitions.length
          ? definitions.map(item => descriptor(item, 'field_invalid')) : [text('Common.MessageFieldInvalid')];
      }
    }
    let message = descriptor({...value, code}, fallback);
    if (typeof value?.requestId === 'string' && /^[\w.:-]{1,200}$/.test(value.requestId))
      message += ' ' + text('Common.MessageReference', value.requestId);
    return {message, fields};
  };
  const label = (code, feature = 'Common') => {
    if (code === null || code === undefined || code === '') return '—';
    const labels = globalThis.UnionStackUi?.displayLabels || {};
    const suffix = String(code).toLowerCase();
    const key = `${feature}:${suffix}`.toLowerCase();
    return Object.hasOwn(labels, key) ? labels[key]
      : Object.hasOwn(labels, `common:${suffix}`) ? labels[`common:${suffix}`] : text('Common.StatusUnknown');
  };
  const html = (key, ...args) => text(key, ...args).replace(/[&<>"']/g,
    character => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'})[character]);
  const localErrors = new WeakSet();
  const error = (key, ...args) => {
    const failure = new Error(text(key, ...args));
    localErrors.add(failure);
    return failure;
  };
  const rangeError = (key, ...args) => {
    const failure = new RangeError(text(key, ...args));
    localErrors.add(failure);
    return failure;
  };
  const problemError = (value, status = 500) => {
    const localized = problem(value, status);
    const failure = new Error(localized.message);
    failure.fields = localized.fields;
    localErrors.add(failure);
    return failure;
  };
  const localMessage = failure => failure instanceof Error && localErrors.has(failure) ? failure.message : undefined;
  globalThis.UnionStackI18n = Object.freeze({text, format, problem, label, html, error, rangeError, problemError, localMessage});
})();
